import { Injectable } from "@angular/core";

export type ScaleUnit = "oz" | "lb" | "gr" | "kg";

export interface ScaleReading {
  weight: number;
  unit: ScaleUnit;
  stable: boolean;
}

const DYMO_VENDOR_ID = 0x0922;
const DYMO_PRODUCTS = [
  { productId: 0x8003, name: "DYMO M10" },
  { productId: 0x8004, name: "DYMO M25" },
  { productId: 0x8009, name: "DYMO S100/S250" },
];

@Injectable({ providedIn: "root" })
export class ScaleService {
  private device: HIDDevice | null = null;
  private disconnectListener: ((event: HIDConnectionEvent) => void) | null = null;
  deviceName: string | null = null;

  get isSupported(): boolean {
    return "hid" in navigator;
  }

  get isConnected(): boolean {
    return this.device?.opened === true;
  }

  /**
   * Connect to a DYMO scale. First call opens the browser device picker;
   * subsequent calls reuse the cached device.
   * Must be called from a user gesture (click handler).
   */
  async connect(): Promise<boolean> {
    if (!this.isSupported) {
      throw new Error("WebHID is not supported in this browser. Use Chrome or Edge.");
    }

    if (this.device?.opened) {
      return true;
    }

    // Clear any stale device reference
    if (this.device) {
      try { await this.device.close(); } catch { /* already closed */ }
      this.device = null;
      this.deviceName = null;
    }

    const filters = DYMO_PRODUCTS.map((p) => ({
      vendorId: DYMO_VENDOR_ID,
      productId: p.productId,
    }));

    // Try previously paired device first (no picker needed)
    const devices = await navigator.hid.getDevices();
    let device = devices.find(
      (d) =>
        d.vendorId === DYMO_VENDOR_ID &&
        DYMO_PRODUCTS.some((p) => p.productId === d.productId)
    );

    if (!device) {
      // Opens browser device picker (requires user gesture)
      const selected = await navigator.hid.requestDevice({ filters });
      device = selected[0];
    }

    if (!device) {
      return false; // User cancelled picker
    }

    try {
      if (!device.opened) {
        await device.open();
      }
    } catch {
      // Device exists in browser permissions but is physically gone
      return false;
    }

    this.device = device;
    this.deviceName =
      DYMO_PRODUCTS.find((p) => p.productId === device.productId)?.name ??
      `DYMO Scale`;

    // Remove previous listener to avoid duplicates
    if (this.disconnectListener) {
      navigator.hid.removeEventListener("disconnect", this.disconnectListener);
    }
    this.disconnectListener = (event: HIDConnectionEvent) => {
      if (event.device === this.device) {
        this.device = null;
        this.deviceName = null;
      }
    };
    navigator.hid.addEventListener("disconnect", this.disconnectListener);

    return true;
  }

  /**
   * Read the current weight from the scale.
   * Listens for one inputreport event with a 3-second timeout.
   *
   * WebHID strips the report ID byte, so event.data is 5 bytes:
   *   [0] status  (2=zero, 4=positive/stable, 5=negative)
   *   [1] unit    (2=grams, 3=kilograms, 11=ounces, 12=pounds)
   *   [2] scaling exponent (signed byte)
   *   [3] weight low byte
   *   [4] weight high byte
   */
  async readWeight(): Promise<ScaleReading> {
    if (!this.device?.opened) {
      throw new Error("Scale is not connected.");
    }

    return new Promise<ScaleReading>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.device!.removeEventListener("inputreport", handler);
        // Clear stale device so next connect() will re-pair
        this.device = null;
        this.deviceName = null;
        reject(new Error("Scale read timed out. Reconnect and try again."));
      }, 3000);

      const handler = (event: HIDInputReportEvent) => {
        clearTimeout(timeout);
        this.device!.removeEventListener("inputreport", handler);

        const data = new Uint8Array(event.data.buffer, event.data.byteOffset, event.data.byteLength);
        const statusByte = data[0];
        const unitByte = data[1];

        let scalingExponent = data[2];
        if (scalingExponent > 127) {
          scalingExponent = scalingExponent - 256;
        }
        const scalingFactor = Math.pow(10, scalingExponent);

        const rawWeight = data[3] + data[4] * 256;
        const stable = statusByte === 2 || statusByte === 4 || statusByte === 5;

        let unit: ScaleUnit;
        switch (unitByte) {
          case 2: unit = "gr"; break;
          case 3: unit = "kg"; break;
          case 11: unit = "oz"; break;
          case 12: unit = "lb"; break;
          default: unit = "oz"; break;
        }
        const sign = statusByte === 5 ? -1 : 1;
        const weight = sign * rawWeight * scalingFactor;

        resolve({ weight, unit, stable });
      };

      this.device!.addEventListener("inputreport", handler);
    });
  }

  /**
   * Convert a weight from the scale's native unit to
   * the dialog's selected UOM (oz, lb, gr, kg).
   */
  static convertWeight(value: number, fromUnit: ScaleUnit, toUnit: string): number {
    // Convert to grams as common intermediate
    let grams: number;
    switch (fromUnit) {
      case "oz": grams = value * 28.3495; break;
      case "lb": grams = value * 453.592; break;
      case "kg": grams = value * 1000; break;
      case "gr": default: grams = value; break;
    }

    switch (toUnit) {
      case "oz":
        return +(grams / 28.3495).toFixed(2);
      case "lb":
        return +(grams / 453.592).toFixed(2);
      case "gr":
        return +grams.toFixed(2);
      case "kg":
        return +(grams / 1000).toFixed(3);
      default:
        return +(grams / 28.3495).toFixed(2);
    }
  }

  async disconnect(): Promise<void> {
    if (this.device?.opened) {
      await this.device.close();
    }
    this.device = null;
    this.deviceName = null;
  }
}
